// import React, { Suspense, useEffect, useRef, useState } from "react";
// import { Routes, Route, Outlet, Navigate, useLocation } from "react-router-dom";
// import { useAuth } from "../../context/AuthContext";
// import Loading from "../Loading/Loading";
// import {
//     publicRoutes,
//     protectedRoutes,
//     preloadRoute,
//     getBaseKey,
// } from "../../routes/pageRoutes";

// if ("scrollRestoration" in window.history)
//     window.history.scrollRestoration = "manual";

// /* ---------------- tuning ---------------- */
// const WAVE_MS = 1300; // shockwave duration
// const IN_MS = 650; // new page settle duration
// const PRE_WAIT_MS = 150; // minimum hold on the old page
// const DISPLACE_PX = 420; // peak displacement (filter scale; max shift = half)
// const MW = 128,
//     MH = 72; // low-res map size (browser upscales it smoothly)
// const OUTER_EASE = 2.2; // leading edge: fast, decelerating
// const INNER_EASE = 1.45; // trailing edge: slower, so the band stretches
// const INNER_DELAY = 0.07; // trailing edge starts slightly later
// const RIPPLE_LEN = 0.45; // length of trailing ripples (screen heights)
// const TAIL = 0.07; // small pinch in front of the leading edge
// const WOBBLE = 0.022; // irregularity of the ring (screen heights)
// const ZOOM = 0.09; // camera push on the old page
// const GLOW_EDGE = [175, 175, 175];
// const GLOW_CREST = [255, 255, 255];
// const GRID_CELL = 40; // grid tile size in px
// const GRID_FILL = 0.22; // tile fill strength at the crest
// const GRID_LINE = 1; // grid line width in px
// const GRID_DPR_MAX = 1.5; // cap resolution for performance

// /* ---------------- helpers ---------------- */
// const clamp01 = (v) => Math.min(1, Math.max(0, v));
// const smooth = (a, b, x) => {
//     const t = clamp01((x - a) / (b - a));
//     return t * t * (3 - 2 * t);
// };
// const easeOut = (t) => 1 - Math.pow(1 - t, 2.2);
// const wait = (ms) => new Promise((r) => setTimeout(r, ms));
// const nextFrames = () =>
//     new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

// const scrollToTop = () => {
//     window.scrollTo({ top: 0, left: 0, behavior: "instant" });
//     document.documentElement.scrollTo({ top: 0, left: 0, behavior: "instant" });
//     document.body.scrollTo({ top: 0, left: 0, behavior: "instant" });
// };

// const runPhase = (duration, step, isCancelled) =>
//     new Promise((resolve) => {
//         const t0 = performance.now();
//         const frame = (now) => {
//             if (isCancelled()) return resolve();
//             const p = Math.min(1, (now - t0) / duration);
//             step(p);
//             if (p < 1) requestAnimationFrame(frame);
//             else resolve();
//         };
//         requestAnimationFrame(frame);
//     });

// const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(
//     navigator.userAgent,
// );
// const prefersReduced =
//     window.matchMedia &&
//     window.matchMedia("(prefers-reduced-motion: reduce)").matches;
// const lowEnd =
//     (navigator.hardwareConcurrency || 8) <= 4 ||
//     (navigator.deviceMemory || 8) <= 2;
// // Safari's feDisplacementMap on HTML is unreliable; weak devices skip the warp
// let warpAllowed = !isSafari && !prefersReduced && !lowEnd;

// /* ---------------- lookup tables (built once) ---------------- */
// // Replaces per-pixel pow/sin/exp with a single array read.
// const PN = 256;
// const PROFILE = new Float32Array(PN + 1); // main wall shape
// const CREST = new Float32Array(PN + 1); // glow crest shape
// const RIPPLE = new Float32Array(PN + 1); // trailing ripples
// const PINCH = new Float32Array(PN + 1); // pinch ahead of the front
// for (let i = 0; i <= PN; i++) {
//     const u = i / PN;
//     const s = Math.max(0, Math.sin(Math.PI * Math.pow(u, 0.65)));
//     PROFILE[i] = Math.pow(s, 1.3);
//     CREST[i] = Math.pow(s, 5);
//     const k = u * RIPPLE_LEN;
//     RIPPLE[i] = Math.sin(k * 36) * Math.exp(-k * 9) * 0.32;
//     PINCH[i] = -0.2 * Math.sin(Math.PI * u);
// }

// /* Stable module-level handlers so add/removeEventListener always match */
// const preventScroll = (e) => e.preventDefault();
// const preventScrollKeys = (e) => {
//     if (/^(INPUT|TEXTAREA|SELECT)$/.test(e.target && e.target.tagName)) return;
//     if (
//         [
//             " ",
//             "PageDown",
//             "PageUp",
//             "ArrowDown",
//             "ArrowUp",
//             "Home",
//             "End",
//         ].includes(e.key)
//     )
//         e.preventDefault();
// };

// const RequireAuth = () => {
//     const { currentUser, loading } = useAuth();
//     if (loading) return <Loading />;
//     if (!currentUser) return <Navigate to="/competitions" replace />;
//     return <Outlet />;
// };

// /* Static per-pixel geometry, cached per aspect ratio */
// let geoCache = null;
// const getGeometry = (aspect) => {
//     const key = aspect.toFixed(3);
//     if (geoCache && geoCache.key === key) return geoCache;
//     const n = MW * MH;
//     const dist = new Float32Array(n);
//     const dx = new Float32Array(n);
//     const dy = new Float32Array(n);
//     const s3 = new Float32Array(n),
//         c3 = new Float32Array(n),
//         s5 = new Float32Array(n),
//         c5 = new Float32Array(n),
//         s9 = new Float32Array(n),
//         c9 = new Float32Array(n);
//     for (let py = 0; py < MH; py++) {
//         for (let px = 0; px < MW; px++) {
//             const x = ((px + 0.5) / MW - 0.5) * aspect;
//             const y = (py + 0.5) / MH - 0.5;
//             const d = Math.hypot(x, y) || 1e-6;
//             const i = py * MW + px;
//             const a = Math.atan2(y, x);
//             dist[i] = d;
//             dx[i] = x / d;
//             dy[i] = y / d;
//             s3[i] = Math.sin(3 * a);
//             c3[i] = Math.cos(3 * a);
//             s5[i] = Math.sin(5 * a);
//             c5[i] = Math.cos(5 * a);
//             s9[i] = Math.sin(9 * a);
//             c9[i] = Math.cos(9 * a);
//         }
//     }
//     geoCache = {
//         key,
//         dist,
//         dx,
//         dy,
//         s3,
//         c3,
//         s5,
//         c5,
//         s9,
//         c9,
//         corner: Math.hypot(aspect / 2, 0.5),
//     };
//     return geoCache;
// };

// const AnimatedRoutes = () => {
//     const location = useLocation();
//     const [displayLocation, setDisplayLocation] = useState(location);
//     const displayRef = useRef(location);

//     const stageRef = useRef(null);
//     const wrapRef = useRef(null);
//     const innerRef = useRef(null);
//     const backdropRef = useRef(null);
//     const spacerRef = useRef(null);
//     const filterRef = useRef(null);
//     const feImageRef = useRef(null);
//     const glowRef = useRef(null);
//     const mapCanvas = useRef(null);
//     const mapCtx = useRef(null);
//     const glowCtx = useRef(null);
//     const mapImg = useRef(null);
//     const glowSize = useRef({ w: 1, h: 1, dpr: 1 });

//     // Allocate canvases/ImageData once; precompute geometry while the browser is idle
//     useEffect(() => {
//         mapCtx.current = mapCanvas.current.getContext("2d");
//         glowCtx.current = glowRef.current.getContext("2d");
//         mapImg.current = mapCtx.current.createImageData(MW, MH);
//         const idle = window.requestIdleCallback || ((f) => setTimeout(f, 200));
//         const h = idle(() =>
//             getGeometry(window.innerWidth / window.innerHeight),
//         );
//         return () => {
//             if (window.cancelIdleCallback && typeof h === "number")
//                 window.cancelIdleCallback(h);
//         };
//     }, []);

//     const setMapHref = (url) => {
//         const el = feImageRef.current;
//         if (!el) return;
//         el.setAttribute("href", url);
//         el.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", url);
//     };

//     // Size the glow canvas to the real viewport so grid lines stay crisp
//     const sizeGlowCanvas = (w, h) => {
//         const dpr = Math.min(window.devicePixelRatio || 1, GRID_DPR_MAX);
//         const c = glowRef.current;
//         c.width = Math.round(w * dpr);
//         c.height = Math.round(h * dpr);
//         glowCtx.current.setTransform(dpr, 0, 0, dpr, 0, 0);
//         glowSize.current = { w, h, dpr };
//     };

//     const enterViewportMode = (sy) => {
//         const st = stageRef.current;
//         const w = wrapRef.current;
//         const inner = innerRef.current;
//         const sp = spacerRef.current;
//         if (!st || !w || !inner || !sp) return;
//         sp.style.height = w.getBoundingClientRect().height + "px";
//         sp.style.display = "block";
//         // The untransformed STAGE is the viewport-sized, clipping box. It clips the
//         // zoomed/shaken wrapper, so nothing can ever add a horizontal scrollbar and
//         // no <html>/<body> overflow has to be touched.
//         st.style.position = "absolute";
//         st.style.top = `${sy}px`;
//         st.style.left = "0";
//         st.style.width = "100%";
//         st.style.height = document.documentElement.clientHeight + "px";
//         st.style.overflow = "clip";
//         w.style.minHeight = "0";
//         w.style.height = "100%";
//         w.style.overflow = "clip";
//         inner.style.position = "relative";
//         inner.style.top = `-${sy}px`;
//         window.addEventListener("wheel", preventScroll, { passive: false });
//         window.addEventListener("touchmove", preventScroll, { passive: false });
//         window.addEventListener("keydown", preventScrollKeys);
//     };

//     const exitViewportMode = () => {
//         const st = stageRef.current;
//         const w = wrapRef.current;
//         const inner = innerRef.current;
//         const sp = spacerRef.current;
//         window.removeEventListener("wheel", preventScroll);
//         window.removeEventListener("touchmove", preventScroll);
//         window.removeEventListener("keydown", preventScrollKeys);
//         if (st) {
//             st.style.position = "";
//             st.style.top = "";
//             st.style.left = "";
//             st.style.width = "";
//             st.style.height = "";
//             st.style.overflow = "";
//         }
//         if (w) {
//             w.style.minHeight = "";
//             w.style.height = "";
//             w.style.overflow = "";
//         }
//         if (inner) {
//             inner.style.position = "";
//             inner.style.top = "";
//         }
//         if (sp) {
//             sp.style.display = "none";
//             sp.style.height = "0";
//         }
//     };

//     const resetStyles = () => {
//         const w = wrapRef.current;
//         if (w) {
//             w.style.filter = "";
//             w.style.transform = "";
//             w.style.opacity = "";
//             w.style.willChange = "";
//             w.style.transformOrigin = "";
//         }
//         delete document.documentElement.dataset.pageTransition;
//         exitViewportMode();
//         if (backdropRef.current) backdropRef.current.style.opacity = "0";
//         if (glowRef.current) glowRef.current.style.opacity = "0";
//     };

//     const drawWave = (p, g, rnd, warp) => {
//         const RMAX = g.corner + 0.2;

//         const Ro = (1 - Math.pow(1 - p, OUTER_EASE)) * RMAX;
//         const pi = clamp01((p - INNER_DELAY) / (1 - INNER_DELAY));
//         const Ri = Math.max(
//             0,
//             Math.min(
//                 (1 - Math.pow(1 - pi, INNER_EASE)) * RMAX * 0.97,
//                 Ro - 0.015,
//             ),
//         );
//         const band = Math.max(1e-4, Ro - Ri);
//         const invBand = PN / band;
//         const invRip = (PN - 1) / RIPPLE_LEN;
//         const invTail = (PN - 1) / TAIL;
//         const env = smooth(0, 0.07, p) * (1 - smooth(0.6, 1, p));

//         // ring wobble: 6 per-frame scalars (angle terms are precomputed)
//         const wobAmt = WOBBLE * smooth(0, 0.3, p);
//         const t = p * 6;
//         const q1 = rnd.ph1 + t,
//             q2 = rnd.ph2 - t * 1.3,
//             q3 = rnd.ph3 + t * 2;
//         const a3s = wobAmt * 0.55 * Math.cos(q1),
//             a3c = wobAmt * 0.55 * Math.sin(q1);
//         const a5s = wobAmt * 0.3 * Math.cos(q2),
//             a5c = wobAmt * 0.3 * Math.sin(q2);
//         const a9s = wobAmt * 0.15 * Math.cos(q3),
//             a9c = wobAmt * 0.15 * Math.sin(q3);

//         /* ---- displacement map (unchanged behaviour) ---- */
//         if (warp) {
//             const md = mapImg.current.data;
//             const { dist, dx, dy, s3, c3, s5, c5, s9, c9 } = g;
//             const lo = Ri - RIPPLE_LEN;
//             const hi = Ro + TAIL;

//             for (let i = 0, n = MW * MH; i < n; i++) {
//                 const o = i * 4;
//                 const d =
//                     dist[i] +
//                     s3[i] * a3s +
//                     c3[i] * a3c +
//                     s5[i] * a5s +
//                     c5[i] * a5c +
//                     s9[i] * a9s +
//                     c9[i] * a9c;

//                 let bulge = 0;
//                 if (d > lo && d < hi) {
//                     if (d >= Ri) {
//                         if (d <= Ro) {
//                             bulge = PROFILE[((d - Ri) * invBand) | 0] * env;
//                         } else {
//                             bulge = PINCH[((d - Ro) * invTail) | 0] * env;
//                         }
//                     } else {
//                         bulge = RIPPLE[((Ri - d) * invRip) | 0] * env;
//                     }
//                 }
//                 md[o] = 127.5 - dx[i] * bulge * 127.5;
//                 md[o + 1] = 127.5 - dy[i] * bulge * 127.5;
//                 md[o + 2] = 128;
//                 md[o + 3] = 255;
//             }
//             mapCtx.current.putImageData(mapImg.current, 0, 0);
//             setMapHref(mapCanvas.current.toDataURL("image/png"));
//         }

//         /* ---- grid crest: tiles lit only inside the Ri..Ro band ---- */
//         const ctx = glowCtx.current;
//         const { w: W, h: H } = glowSize.current;
//         const aspect = W / H;
//         ctx.clearRect(0, 0, W, H);
//         ctx.lineWidth = GRID_LINE;

//         const cols = Math.ceil(W / GRID_CELL);
//         const rows = Math.ceil(H / GRID_CELL);

//         for (let r = 0; r < rows; r++) {
//             const y0 = r * GRID_CELL;
//             const y = (y0 + GRID_CELL / 2) / H - 0.5;
//             for (let c = 0; c < cols; c++) {
//                 const x0 = c * GRID_CELL;
//                 const x = ((x0 + GRID_CELL / 2) / W - 0.5) * aspect;
//                 const a = Math.atan2(y, x);
//                 const d =
//                     Math.hypot(x, y) +
//                     Math.sin(3 * a) * a3s +
//                     Math.cos(3 * a) * a3c +
//                     Math.sin(5 * a) * a5s +
//                     Math.cos(5 * a) * a5c +
//                     Math.sin(9 * a) * a9s +
//                     Math.cos(9 * a) * a9c;

//                 if (d < Ri || d > Ro) continue;

//                 const idx = ((d - Ri) * invBand) | 0;
//                 const prof = PROFILE[idx];
//                 const crest = CREST[idx];

//                 const lineA = (0.25 * prof + 0.75 * crest) * env;
//                 if (lineA < 0.01) continue;

//                 const col = Math.round(
//                     GLOW_EDGE[0] + (GLOW_CREST[0] - GLOW_EDGE[0]) * crest,
//                 );
//                 const fillA = crest * crest * GRID_FILL * env;

//                 if (fillA > 0.005) {
//                     ctx.fillStyle = `rgba(${col},${col},${col},${fillA.toFixed(3)})`;
//                     ctx.fillRect(x0 + 1, y0 + 1, GRID_CELL - 2, GRID_CELL - 2);
//                 }
//                 ctx.strokeStyle = `rgba(${col},${col},${col},${lineA.toFixed(3)})`;
//                 ctx.strokeRect(
//                     x0 + 0.5,
//                     y0 + 0.5,
//                     GRID_CELL - 1,
//                     GRID_CELL - 1,
//                 );
//             }
//         }
//     };

//     useEffect(() => {
//         if (location === displayRef.current) {
//             resetStyles(); // e.g. user navigated back mid-transition
//             return;
//         }
//         let cancelled = false;
//         let inAnim = null;
//         const isCancelled = () => cancelled;

//         (async () => {
//             resetStyles();

//             await Promise.all([
//                 preloadRoute(location.pathname),
//                 wait(PRE_WAIT_MS),
//             ]);
//             if (cancelled) return;

//             const sameSection =
//                 getBaseKey(location.pathname) ===
//                 getBaseKey(displayRef.current.pathname);
//             if (sameSection) {
//                 displayRef.current = location;
//                 setDisplayLocation(location);
//                 scrollToTop();
//                 return;
//             }

//             const wrap = wrapRef.current;
//             const inner = innerRef.current;
//             const sy = Math.max(0, window.scrollY || 0);

//             // lock the wrapper to the viewport (see enterViewportMode)
//             enterViewportMode(sy);
//             // lets heavy pages (WebGL, video) pause themselves while the wave runs
//             document.documentElement.dataset.pageTransition = "wave";
//             window.dispatchEvent(new Event("page-transition-start"));
//             const fw = wrap.clientWidth;
//             const fh = wrap.clientHeight;
//             const g = getGeometry(fw / fh);
//             sizeGlowCanvas(fw, fh);
//             const rnd = {
//                 ph1: Math.random() * Math.PI * 2,
//                 ph2: Math.random() * Math.PI * 2,
//                 ph3: Math.random() * Math.PI * 2,
//                 sx: (Math.random() - 0.5) * 2,
//                 sy: (Math.random() - 0.5) * 2,
//             };
//             let warp = warpAllowed;

//             // Dark backdrop for the fade, but ONLY if the page has no body background of
//             // its own (pages like ContactUs paint their art on <body>; it must stay visible).
//             const bodyHasBg =
//                 getComputedStyle(document.body).backgroundImage !== "none";
//             backdropRef.current.style.opacity = bodyHasBg ? "0" : "1";

//             wrap.style.transformOrigin = "50% 50%";
//             wrap.style.willChange = "transform, opacity";
//             if (warp) {
//                 // wrapper is viewport-sized, so the filter region is simply the screen
//                 const f = filterRef.current;
//                 f.setAttribute("x", "0");
//                 f.setAttribute("y", "0");
//                 f.setAttribute("width", String(fw));
//                 f.setAttribute("height", String(fh));
//                 const fi = feImageRef.current;
//                 fi.setAttribute("x", "0");
//                 fi.setAttribute("y", "0");
//                 fi.setAttribute("width", String(fw));
//                 fi.setAttribute("height", String(fh));
//                 wrap.style.filter = "url(#shockwave-filter)";
//             }
//             glowRef.current.style.opacity = "1";

//             // 1. shockwave across the OLD page
//             let last = 0,
//                 frames = 0,
//                 slow = 0;
//             await runPhase(
//                 WAVE_MS,
//                 (p) => {
//                     // Frame timing only decides the NEXT transition; the running one is
//                     // never altered (dropping the warp mid-run looked like it broke).
//                     const now = performance.now();
//                     if (last) {
//                         frames++;
//                         if (now - last > 40) slow++;
//                     }
//                     last = now;

//                     drawWave(p, g, rnd, warp);
//                     wrap.style.opacity = (1 - smooth(0.6, 1, p)).toFixed(3);
//                     const shake = Math.exp(-p * 7) * smooth(0, 0.04, p);
//                     const tx = Math.sin(p * 70) * 7 * shake * rnd.sx;
//                     const ty = Math.cos(p * 63) * 7 * shake * rnd.sy;
//                     const zoom = 1 + ZOOM * easeOut(p) + 0.025 * shake;
//                     wrap.style.transform = `translate3d(${tx.toFixed(1)}px, ${ty.toFixed(1)}px, 0) scale(${zoom.toFixed(4)})`;
//                 },
//                 isCancelled,
//             );
//             if (cancelled) return;

//             // heavy pages only get the lite effect on the NEXT transition
//             if (frames >= 10 && slow / frames > 0.7) warpAllowed = false;
//             delete document.documentElement.dataset.pageTransition;

//             // 2. swap while invisible
//             wrap.style.filter = "";
//             glowRef.current.style.opacity = "0";
//             wrap.style.opacity = "0";
//             inner.style.top = "0px";
//             stageRef.current.style.top = "0px";
//             scrollToTop();
//             displayRef.current = location;
//             setDisplayLocation(location);
//             await nextFrames();
//             if (cancelled) return;
//             // size the spacer to the NEW page so ScrollTrigger etc. see the real scroll height
//             spacerRef.current.style.height =
//                 Math.max(
//                     inner.getBoundingClientRect().height,
//                     window.innerHeight,
//                 ) + "px";
//             scrollToTop();

//             // 3. new page settles in: Web Animations API runs on the compositor,
//             //    so it stays smooth even while the new page is mounting/painting.
//             inAnim = wrap.animate(
//                 [
//                     {
//                         opacity: 0,
//                         transform: `scale(${1 + ZOOM})`,
//                         easing: "cubic-bezier(0.2, 0.8, 0.2, 1)",
//                     },
//                     {
//                         opacity: 1,
//                         transform: "scale(0.994)",
//                         offset: 0.72,
//                         easing: "ease-out",
//                     },
//                     { opacity: 1, transform: "scale(1)" },
//                 ],
//                 { duration: IN_MS, fill: "forwards" },
//             );
//             try {
//                 await inAnim.finished;
//             } catch (e) {
//                 return; // cancelled
//             }
//             if (!cancelled) {
//                 resetStyles(); // transform/filter back to none for fixed-position pages
//                 inAnim.cancel();
//             }
//         })();

//         return () => {
//             cancelled = true;
//             if (inAnim) inAnim.cancel();
//         };
//     }, [location]);

//     return (
//         <div
//             style={{
//                 // no background here: it used to hide any background a page paints on <body>
//                 minHeight: "100vh",
//                 overflowX: "clip",
//             }}
//         >
//             {/* transition-only backdrop, sits behind page content (z-index -1) */}
//             <div
//                 ref={backdropRef}
//                 aria-hidden="true"
//                 style={{
//                     position: "fixed",
//                     inset: 0,
//                     background: "#000",
//                     zIndex: -1,
//                     opacity: 0,
//                     pointerEvents: "none",
//                 }}
//             />
//             {/* displacement filter: the ring map (already opaque neutral grey) drives feDisplacementMap */}
//             <svg
//                 aria-hidden="true"
//                 width="0"
//                 height="0"
//                 style={{ position: "absolute" }}
//             >
//                 <filter
//                     id="shockwave-filter"
//                     ref={filterRef}
//                     filterUnits="userSpaceOnUse"
//                     colorInterpolationFilters="sRGB"
//                     x="0"
//                     y="0"
//                     width="100%"
//                     height="100%"
//                 >
//                     <feImage
//                         ref={feImageRef}
//                         preserveAspectRatio="none"
//                         result="map"
//                     />
//                     <feDisplacementMap
//                         in="SourceGraphic"
//                         in2="map"
//                         scale={DISPLACE_PX}
//                         xChannelSelector="R"
//                         yChannelSelector="G"
//                     />
//                 </filter>
//             </svg>

//             {/* offscreen canvas that holds the displacement map */}
//             <canvas
//                 ref={mapCanvas}
//                 width={MW}
//                 height={MH}
//                 style={{ display: "none" }}
//             />

//             <div ref={stageRef}>
//                 <div ref={wrapRef} style={{ minHeight: "100vh" }}>
//                     <div ref={innerRef}>
//                         <Suspense fallback={<Loading />}>
//                             <Routes location={displayLocation}>
//                                 {publicRoutes.map(({ path, Page }) => (
//                                     <Route
//                                         key={path}
//                                         path={path}
//                                         element={<Page />}
//                                     />
//                                 ))}
//                                 <Route element={<RequireAuth />}>
//                                     {protectedRoutes.map(({ path, Page }) => (
//                                         <Route
//                                             key={path}
//                                             path={path}
//                                             element={<Page />}
//                                         />
//                                     ))}
//                                 </Route>
//                             </Routes>
//                         </Suspense>
//                     </div>
//                 </div>
//             </div>
//             {/* keeps document height while the wrapper is viewport-locked */}
//             <div ref={spacerRef} style={{ display: "none" }} />

//             {/* grid crest of the wave, drawn above everything (sized in code) */}
//             <canvas
//                 ref={glowRef}
//                 style={{
//                     position: "fixed",
//                     inset: 0,
//                     width: "100vw",
//                     height: "100vh",
//                     pointerEvents: "none",
//                     zIndex: 9998,
//                     opacity: 0,
//                     mixBlendMode: "screen",
//                 }}
//             />
//         </div>
//     );
// };

// export default AnimatedRoutes;

import React, { Suspense, useEffect, useRef, useState } from "react";
import { Routes, Route, Outlet, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import Loading from "../Loading/Loading";
import {
    publicRoutes,
    protectedRoutes,
    preloadRoute,
} from "../../routes/pageRoutes";

if ("scrollRestoration" in window.history)
    window.history.scrollRestoration = "manual";

const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    document.documentElement.scrollTo({ top: 0, left: 0, behavior: "instant" });
    document.body.scrollTo({ top: 0, left: 0, behavior: "instant" });
};

const RequireAuth = () => {
    const { currentUser, loading } = useAuth();
    if (loading) return <Loading />;
    if (!currentUser) return <Navigate to="/competitions" replace />;
    return <Outlet />;
};

const AnimatedRoutes = () => {
    const location = useLocation();
    const [displayLocation, setDisplayLocation] = useState(location);
    const displayRef = useRef(location);

    useEffect(() => {
        if (location === displayRef.current) return;
        let cancelled = false;

        (async () => {
            // load the next page's chunk first so there's no loader flash
            await preloadRoute(location.pathname);
            if (cancelled) return;

            displayRef.current = location;
            setDisplayLocation(location);
            scrollToTop();
        })();

        return () => {
            cancelled = true;
        };
    }, [location]);

    return (
        <div style={{ minHeight: "100vh", overflowX: "clip" }}>
            <Suspense fallback={<Loading />}>
                <Routes location={displayLocation}>
                    {publicRoutes.map(({ path, Page }) => (
                        <Route key={path} path={path} element={<Page />} />
                    ))}
                    <Route element={<RequireAuth />}>
                        {protectedRoutes.map(({ path, Page }) => (
                            <Route key={path} path={path} element={<Page />} />
                        ))}
                    </Route>
                </Routes>
            </Suspense>
        </div>
    );
};

export default AnimatedRoutes;
